import React, { useState, useRef, useEffect, useCallback } from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import AppointmentRow from './AppointmentRow';
import PendingRequestItem from './PendingRequestItem';
import ReservationItem from './ReservationItem';
import CanceledItem from './CanceledItem';
import { formatDayLabel, isSameDay } from '../../utils/dashboardDates';
import styles from './ActionCenter.module.css';

// Conta grande (centenas de consultas no dia): cada aba mostra os primeiros e "Ver todos"
const LIST_LIMIT = 8;

const EMPTY_TEXT = {
  confirm: 'Todos os agendamentos de hoje e do próximo dia útil estão confirmados.',
  requests: 'Nenhum paciente pediu para reagendar ou cancelar pelo link.',
  reservations: 'Nenhuma reserva aguardando cadastro.',
  canceled: 'Ninguém deixou de reagendar depois de cancelar.',
};

/** Agrupa os agendamentos por dia, mantendo a ordem ("Hoje", "Amanhã", ...). */
function groupByDay(list) {
  return list.reduce((groups, appointment) => {
    const last = groups[groups.length - 1];
    if (last && isSameDay(last.date, appointment.date)) {
      last.items.push(appointment);
    } else {
      groups.push({ date: appointment.date, items: [appointment] });
    }
    return groups;
  }, []);
}

function EmptyState({ title, text }) {
  return (
    <div className={styles.empty}>
      <span className={styles.emptyIcon}><CheckRoundedIcon style={{ fontSize: 22 }} /></span>
      <span className={styles.emptyTitle}>{title}</span>
      <span className={styles.emptyText}>{text}</span>
    </div>
  );
}

/**
 * "Para resolver": o que depende de alguém da clínica agora — confirmações,
 * pedidos feitos pelo paciente no link, reservas aguardando cadastro e
 * cancelamentos sem reagendamento. Abre na primeira aba que tem itens.
 */
function ActionCenter({ toConfirm, pendingRequests, reservations = [], canceled }) {
  const [resolvedIds, setResolvedIds] = useState([]);
  const [releasedIds, setReleasedIds] = useState([]);
  const requests = pendingRequests.filter((item) => !resolvedIds.includes(item.id));
  const pendingReservations = reservations.filter((item) => !releasedIds.includes(item.id));

  // No celular as abas rolam de lado: sombra e seta só quando há aba escondida daquele lado.
  const tabsRef = useRef(null);
  const [hidden, setHidden] = useState({ left: false, right: false });
  const updateHidden = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setHidden((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    updateHidden();
    const el = tabsRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateHidden);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateHidden]);

  const tabs = [
    { key: 'confirm', label: 'Confirmar', count: toConfirm.length },
    { key: 'requests', label: 'Pedidos dos pacientes', count: requests.length },
    { key: 'reservations', label: 'Reservas', count: pendingReservations.length },
    { key: 'canceled', label: 'Cancelados', count: canceled.length },
  ];
  const total = tabs.reduce((sum, tab) => sum + tab.count, 0);
  const [active, setActive] = useState(() => (tabs.find((tab) => tab.count > 0) || tabs[0]).key);
  const [expanded, setExpanded] = useState(false);

  function selectTab(key) {
    setActive(key);
    setExpanded(false);
  }

  const cut = (list) => (expanded ? list : list.slice(0, LIST_LIMIT));

  const activeCount = (tabs.find((t) => t.key === active) || tabs[0]).count;

  function renderList() {
    const tab = tabs.find((t) => t.key === active);
    if (tab.count === 0) return <EmptyState title="Nada pendente aqui" text={EMPTY_TEXT[active]} />;

    if (active === 'confirm') {
      return groupByDay(cut(toConfirm)).map((group) => (
        <div key={group.date} className={styles.group}>
          <span className={styles.groupLabel}>{formatDayLabel(group.date)}</span>
          {group.items.map((appointment) => <AppointmentRow key={appointment.id} appointment={appointment} />)}
        </div>
      ));
    }
    if (active === 'requests') {
      return cut(requests).map((item) => (
        <PendingRequestItem key={item.id} item={item} onResolved={(id) => setResolvedIds((prev) => [...prev, id])} />
      ));
    }
    if (active === 'reservations') {
      return cut(pendingReservations).map((item) => (
        <ReservationItem key={item.id} item={item} onResolved={(id) => setReleasedIds((prev) => [...prev, id])} />
      ));
    }
    return cut(canceled).map((item) => <CanceledItem key={item.id} item={item} />);
  }

  return (
    <section className={styles.card} aria-label="Para resolver">
      <header className={styles.header}>
        <div>
          <h2 className={styles.title}>Para resolver</h2>
          <p className={styles.subtitle}>
            {total === 0 ? 'Tudo em ordem por aqui.' : `${total} ${total === 1 ? 'item pede' : 'itens pedem'} sua atenção`}
          </p>
        </div>
      </header>

      <div className={`${styles.tabsWrap} ${hidden.left ? styles.tabsHiddenLeft : ''} ${hidden.right ? styles.tabsHiddenRight : ''}`}>
        <div className={styles.tabs} role="tablist" ref={tabsRef} onScroll={updateHidden}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active === tab.key}
              className={`${styles.tab} ${active === tab.key ? styles.tabActive : ''}`}
              onClick={() => selectTab(tab.key)}
            >
              {tab.label}
              <span className={`${styles.tabCount} ${tab.count > 0 ? styles.tabCountOn : ''}`}>{tab.count}</span>
            </button>
          ))}
        </div>
        {hidden.right && (
          <button
            type="button"
            className={styles.tabsScroll}
            aria-label="Ver mais abas"
            onClick={() => tabsRef.current?.scrollBy({ left: 160, behavior: 'smooth' })}
          >
            <ChevronRightRoundedIcon style={{ fontSize: 20 }} />
          </button>
        )}
      </div>

      {total === 0 ? (
        <EmptyState title="Tudo resolvido" text="Agenda confirmada e nenhum pedido esperando resposta. Bom trabalho." />
      ) : (
        <div className={styles.list} role="tabpanel">
          {renderList()}
          {activeCount > LIST_LIMIT && (
            <button type="button" className={styles.showAll} onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Mostrar menos' : `Ver todos (${activeCount})`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

export default ActionCenter;
